import { Container, Graphics } from "pixi.js";
import { DEFAULT_PITCH_SKIN, pitchSkin, TEAM_IDENTITY, type PitchSkinId } from "@golazo/shared";

/** Fondo de estadio en espacio de pantalla. Solo render, no afecta simulacion. */
export interface StadiumBackdrop {
  container: Container;
  layout(screenWidth: number, screenHeight: number): void;
  update(dt: number): void;
  /** Oleada de grada tras un gol. */
  cheer(intensity?: number): void;
  /** Tension en ultimo tercio / contraataque. */
  swell(intensity: number): void;
}

/**
 * Como va vestido un estadio de la CARRERA (solo aspecto, nunca en online).
 *
 * Sin esto la grada es la de siempre: llena y con los colores del cesped. Con
 * esto cada club tiene su casa: en el barrio hay cuatro padres y muchos
 * asientos vacios, en Primera no cabe un alfiler y la grada alta hace mosaico
 * con los colores del local.
 */
export interface StadiumDressing {
  /** Cuanta gente, de 0 a 1. */
  crowd: number;
  /** Mosaico en la grada alta con los colores del local. */
  tifo: boolean;
  /** Colores del que juega en casa (la mayoria de la grada). */
  local: { primary: number; secondary: number };
  /** Y los del visitante, que siempre trae a unos pocos. */
  visitor: { primary: number; secondary: number };
}

interface CrowdRow {
  y: number;
  height: number;
  phase: number;
  speed: number;
  /** 0 = grada superior, 1 = inferior. */
  band: 0 | 1;
}

const ROWS = 14;

/** Un numero estable 0-1 por asiento: el mismo asiento esta vacio siempre. */
function seatNoise(row: number, col: number): number {
  let h = (row * 374761393 + col * 668265263) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export function createStadiumBackdrop(
  skinId: PitchSkinId = DEFAULT_PITCH_SKIN,
  dressing?: StadiumDressing,
): StadiumBackdrop {
  const skin = pitchSkin(skinId);
  const env = skin.env;
  const container = new Container();
  container.label = "stadium-backdrop";

  const sky = new Graphics();
  const crowd = new Graphics();
  const lights = new Graphics();
  container.addChild(sky, crowd, lights);

  let screenW = 800;
  let screenH = 600;
  let cheerT = 0;
  let swellT = 0;
  let time = 0;

  const rows: CrowdRow[] = Array.from({ length: ROWS }, (_, i) => {
    const band: 0 | 1 = i < ROWS / 2 ? 0 : 1;
    return {
      y: 0,
      height: 0,
      phase: Math.random() * Math.PI * 2,
      speed: 0.6 + Math.random() * 1.4,
      band,
    };
  });

  const drawSky = (): void => {
    sky.clear();
    sky.rect(0, 0, screenW, screenH).fill({ color: env.sky, alpha: 1 });

    // Gradiente vertical aproximado con bandas, del cielo al horizonte.
    const bands = 10;
    for (let i = 0; i < bands; i++) {
      const y0 = (screenH * i) / bands;
      const y1 = (screenH * (i + 1)) / bands;
      const t = i / (bands - 1);
      const color = lerpColor(env.sky, env.skyLow, t);
      sky.rect(0, y0, screenW, y1 - y0 + 1).fill({ color, alpha: 0.85 });
    }

    if (env.venue === "void") {
      // Sin gradas: horizonte de rejilla y sol bajo, estilo synth.
      const horizon = screenH * 0.3;
      sky.circle(screenW * 0.5, horizon, Math.min(screenW, screenH) * 0.22).fill({
        color: env.light,
        alpha: 0.12,
      });
      for (let i = 1; i <= 7; i++) {
        const y = horizon + (i * i * screenH) / 90;
        if (y > screenH) break;
        sky.moveTo(0, y).lineTo(screenW, y).stroke({ width: 1, color: env.light, alpha: 0.1 });
      }
      for (let i = -8; i <= 8; i++) {
        sky
          .moveTo(screenW * 0.5 + i * screenW * 0.03, horizon)
          .lineTo(screenW * 0.5 + i * screenW * 0.4, screenH)
          .stroke({ width: 1, color: env.light, alpha: 0.07 });
      }
      return;
    }

    if (env.venue === "indoor") {
      // Pabellon: pared de fondo, viga de techo y linea de zocalo.
      sky.rect(0, 0, screenW, screenH * 0.2).fill({ color: env.crowd[2], alpha: 0.5 });
      sky.rect(0, screenH * 0.19, screenW, screenH * 0.012).fill({ color: env.light, alpha: 0.22 });
      sky.rect(0, screenH * 0.86, screenW, screenH * 0.14).fill({ color: env.crowd[1], alpha: 0.45 });
      return;
    }

    // Halo de focos desde arriba.
    sky.circle(screenW * 0.5, -screenH * 0.08, screenW * 0.72).fill({
      color: env.crowd[0],
      alpha: 0.06,
    });
    sky.circle(screenW * 0.5, -screenH * 0.05, screenW * 0.48).fill({
      color: env.light,
      alpha: 0.04,
    });
  };

  const drawLights = (): void => {
    lights.clear();
    const spotAlpha = 0.06 + swellT * 0.05;
    const cx = screenW * 0.5;
    const cy = screenH * 0.46;

    if (env.venue === "indoor") {
      // Focos cenitales de pabellon: cuatro elipses de luz sobre la pista.
      for (const fx of [0.22, 0.4, 0.6, 0.78]) {
        lights.ellipse(screenW * fx, cy, screenW * 0.16, screenH * 0.24).fill({
          color: env.light,
          alpha: 0.035 + swellT * 0.02,
        });
      }
    } else {
      // Conos de luz desde las esquinas superiores hacia el centro del campo.
      for (const side of [-1, 1] as const) {
        const ox = side === -1 ? 0 : screenW;
        lights.moveTo(ox, 0);
        lights.lineTo(cx - side * screenW * 0.12, cy);
        lights.lineTo(cx + side * screenW * 0.28, cy + screenH * 0.08);
        lights.lineTo(ox, screenH * 0.22);
        lights.closePath();
        lights.fill({ color: env.light, alpha: spotAlpha });
      }
    }

    // Foco suave sobre el terreno.
    lights.circle(cx, cy, Math.min(screenW, screenH) * 0.38).fill({
      color: env.light,
      alpha: 0.02 + swellT * 0.02,
    });

    // Oscurecer bordes inferiores (profundidad).
    lights.rect(0, screenH * 0.82, screenW, screenH * 0.18).fill({
      color: 0x000000,
      alpha: 0.35,
    });
  };

  const drawCrowd = (): void => {
    crowd.clear();
    // En un "void" no hay publico: el campo flota en el vacio.
    if (env.venue === "void") return;
    const cheer = cheerT;
    const midY = screenH * 0.5;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const bandT = row.band === 0 ? i / (ROWS / 2) : (i - ROWS / 2) / (ROWS / 2);
      const baseY =
        row.band === 0
          ? bandT * midY * 0.42
          : screenH - bandT * midY * 0.38 - row.height;

      const bounce =
        cheer > 0
          ? Math.sin(time * 14 + row.phase) * cheer * (6 + bandT * 4)
          : Math.sin(time * row.speed + row.phase) * 0.6;

      row.y = baseY + bounce;
      row.height = 10 + bandT * 8;

      const width = screenW / (18 + Math.floor(bandT * 6));
      const count = Math.ceil(screenW / width);
      for (let c = 0; c < count; c++) {
        const x = c * width + ((i * 17) % 7);
        const wobble = Math.sin(time * 2.2 + row.phase + c * 0.4) * 1.5;
        const h = row.height + wobble;
        if (dressing) {
          drawDressedSeat(crowd, dressing, i, c, x, row.y, width, h, bandT, cheer);
          continue;
        }
        const tint = env.crowd[(i % 3) as 0 | 1 | 2];
        const headY = row.y + h * 0.35;
        crowd.roundRect(x, row.y, width * 0.72, h, 2).fill({ color: tint, alpha: 0.55 + bandT * 0.2 });
        crowd.circle(x + width * 0.36, headY, 2.2 + bandT * 0.4).fill({
          // La grada lleva los colores de los dos equipos que estan jugando.
          color:
            i % 5 === 0
              ? TEAM_IDENTITY.home.kit.accent
              : i % 5 === 1
                ? TEAM_IDENTITY.away.kit.accent
                : 0x243830,
          alpha: 0.35 + cheer * 0.35,
        });
      }
    }
  };

  drawSky();
  drawLights();
  drawCrowd();

  return {
    container,

    layout(screenWidth: number, screenHeight: number): void {
      screenW = screenWidth;
      screenH = screenHeight;
      drawSky();
      drawLights();
      drawCrowd();
    },

    update(dt: number): void {
      time += dt;
      cheerT = Math.max(0, cheerT - dt * 1.6);
      swellT = Math.max(0, swellT - dt * 1.4);
      drawLights();
      drawCrowd();
    },

    cheer(intensity = 1): void {
      cheerT = Math.min(1, Math.max(cheerT, intensity));
    },

    swell(intensity: number): void {
      swellT = Math.min(1, Math.max(swellT, intensity));
    },
  };
}

/*
 * Un asiento de un estadio de carrera: vacio, un aficionado, o un trozo del
 * mosaico. El mosaico ocupa la parte central de la grada alta (filas 1-4) y
 * son franjas verticales con los dos colores del local: de lejos se lee como
 * una bandera, que es lo unico que tiene que parecer.
 */
function drawDressedSeat(
  g: Graphics,
  d: StadiumDressing,
  row: number,
  col: number,
  x: number,
  y: number,
  width: number,
  h: number,
  bandT: number,
  cheer: number,
): void {
  const inTifo = d.tifo && row >= 1 && row <= 4 && col % 26 >= 5 && col % 26 <= 20;
  if (inTifo) {
    const stripe = Math.floor(col / 2) % 2 === 0 ? d.local.primary : d.local.secondary;
    g.rect(x, y, width + 0.5, h + 2).fill({ color: stripe, alpha: 0.75 + cheer * 0.2 });
    return;
  }
  const noise = seatNoise(row, col);
  if (noise > d.crowd) {
    // Asiento vacio: se ve el plastico, que es lo que mas se ve en el barrio.
    g.roundRect(x, y + h * 0.45, width * 0.7, h * 0.4, 1.5).fill({ color: 0x2a3a32, alpha: 0.35 });
    return;
  }
  // Quien viene: casi todos del local, algunos del visitante (en su esquina).
  const visitorCorner = col % 26 >= 22;
  const pick = seatNoise(col, row + 7);
  const colors = visitorCorner ? d.visitor : d.local;
  const tint = pick < 0.55 ? colors.primary : pick < 0.8 ? colors.secondary : 0x2c3a32;
  g.roundRect(x, y, width * 0.72, h, 2).fill({ color: tint, alpha: 0.5 + bandT * 0.2 });
  g.circle(x + width * 0.36, y + h * 0.35, 2.2 + bandT * 0.4).fill({ color: 0x1c2620, alpha: 0.5 + cheer * 0.3 });
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
